import React, { useEffect, useRef, useState } from 'react'

/** Primitivos interativos só para o catálogo (demo ao vivo, sem API legada). */

export function Field({ label, hint, children }) {
  return (
    <div className="erp-mock-field">
      {label && <span className="erp-mock-label">{label}</span>}
      {children}
      {hint != null && hint !== '' && <span className="erp-mock-hint">{hint}</span>}
    </div>
  )
}

export function Box({ children, className = '' }) {
  return <div className={`erp-mock-box ${className}`.trim()}>{children}</div>
}

export function Flash({ message, tone = 'ok' }) {
  if (!message) return null
  return <div className={`erp-demo-flash is-${tone}`}>{message}</div>
}

export function useFlash(ms = 1600) {
  const [msg, setMsg] = useState(null)
  const [tone, setTone] = useState('ok')
  const t = useRef(null)
  const flash = (text, nextTone = 'ok') => {
    setMsg(text)
    setTone(nextTone)
    clearTimeout(t.current)
    t.current = setTimeout(() => setMsg(null), ms)
  }
  useEffect(() => () => clearTimeout(t.current), [])
  return { msg, tone, flash }
}

export function DemoBtn({
  children,
  variant = 'primary',
  onClick,
  disabled,
  block,
  title,
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      className={`erp-mock-btn erp-mock-btn--${variant}${block ? ' erp-mock-btn--block' : ''}`}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

export function DemoInput({
  value,
  onChange,
  placeholder,
  appearance = 'bordered',
  type = 'text',
  status,
}) {
  const cls = [
    'erp-mock-input',
    'erp-demo-control',
    appearance === 'underline' && 'erp-mock-input--underline',
    appearance === 'filled' && 'erp-mock-input--filled',
    status === 'ok' && 'is-ok',
    status === 'err' && 'is-err',
  ]
    .filter(Boolean)
    .join(' ')
  return (
    <input
      type={type}
      className={cls}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

export function DemoTextarea({ value, onChange, placeholder, maxLength, appearance }) {
  return (
    <div>
      <textarea
        className={`erp-mock-input erp-mock-input--area erp-demo-control${
          appearance === 'resize' ? ' erp-mock-input--resize' : ''
        }`}
        value={value}
        placeholder={placeholder}
        maxLength={maxLength}
        rows={3}
        onChange={(e) => onChange(e.target.value)}
      />
      {maxLength != null && (
        <span className="erp-mock-hint">
          {value.length} / {maxLength}
        </span>
      )}
    </div>
  )
}

export function DemoSelect({ value, onChange, options, searchable }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const filtered = searchable
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : options
  const current = options.find((o) => o.value === value)

  return (
    <div className="erp-demo-select">
      <button
        type="button"
        className={`erp-mock-select erp-demo-control${open ? ' is-open' : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        {current?.label || 'Selecionar…'} ▾
      </button>
      {open && (
        <div className="erp-mock-dropdown">
          {searchable && (
            <div className="erp-demo-dd-search">
              <input
                className="erp-mock-input erp-demo-control"
                placeholder="Filtrar…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                autoFocus
              />
            </div>
          )}
          {filtered.map((o) => (
            <button
              key={o.value}
              type="button"
              className={o.value === value ? 'is-on' : ''}
              onClick={() => {
                onChange(o.value)
                setOpen(false)
                setQ('')
              }}
            >
              {o.label}
            </button>
          ))}
          {!filtered.length && <div className="erp-demo-dd-empty">Nada encontrado</div>}
        </div>
      )}
    </div>
  )
}

export function DemoSwitch({ checked, onChange, labels }) {
  return (
    <button
      type="button"
      className="erp-demo-switch-wrap"
      onClick={() => onChange(!checked)}
      aria-pressed={checked}
    >
      {labels?.[0] && <span className="small">{labels[0]}</span>}
      <span className={`erp-mock-switch${checked ? ' is-on' : ''}`} />
      {labels?.[1] && <span className="small">{labels[1]}</span>}
    </button>
  )
}

export function DemoCheck({ checked, onChange, label }) {
  return (
    <button
      type="button"
      className="erp-mock-checklabel erp-demo-check"
      onClick={() => onChange(!checked)}
    >
      <span className={`erp-mock-check${checked ? ' is-on' : ''}`} />
      {label}
    </button>
  )
}

export function DemoChips({ values, onRemove, onAdd, addLabel = '+' }) {
  return (
    <div className="erp-mock-chips erp-mock-chips--field">
      {values.map((v) => (
        <button
          key={v}
          type="button"
          className="erp-mock-chip erp-demo-chip"
          onClick={() => onRemove(v)}
          title="Remover"
        >
          {v} ×
        </button>
      ))}
      {onAdd && (
        <button type="button" className="erp-mock-chip erp-mock-chip--add erp-demo-chip" onClick={onAdd}>
          {addLabel}
        </button>
      )}
    </div>
  )
}

export function DemoTabs({ tabs, active, onChange, children }) {
  return (
    <div>
      <div className="erp-mock-tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={active === t.id ? 'is-on' : ''}
            onClick={() => onChange(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="erp-mock-tab-panel">{children}</div>
    </div>
  )
}

export function DemoAccordion({ sections }) {
  const [openId, setOpenId] = useState(sections[0]?.id)
  return (
    <div>
      {sections.map((s, i) => {
        const open = openId === s.id
        return (
          <div key={s.id}>
            <button
              type="button"
              className={`erp-mock-acc erp-demo-acc${open ? ' is-open' : ''}`}
              onClick={() => setOpenId(open ? null : s.id)}
            >
              {i + 1}. {s.title} {open ? '▾' : '▸'}
            </button>
            {open && <div className="erp-mock-acc-body">{s.body}</div>}
          </div>
        )
      })}
    </div>
  )
}

/** Grupo de opções exclusivas (modalidade, ASO, etc.) — papel L1 select/radio. */
export function DemoRadioGroup({ name, value, onChange, options, required }) {
  return (
    <div className="erp-demo-radios" role="radiogroup">
      {options.map((o) => (
        <label key={o.value} className={`erp-demo-radio${value === o.value ? ' is-on' : ''}`}>
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={value === o.value}
            required={required}
            onChange={() => onChange(o.value)}
          />
          <span>{o.label}</span>
        </label>
      ))}
    </div>
  )
}

/** Upload com lista — alinhado ao papel L1 upload / L2 form-upload. */
export function DemoFile({
  label,
  hint = 'PDF, JPG ou PNG',
  accept = '.pdf,.jpg,.jpeg,.png,application/pdf,image/*',
  files,
  onChange,
  multiple,
}) {
  return (
    <Field label={label} hint={hint}>
      <div className="erp-mock-drop erp-demo-file">
        <input
          type="file"
          className="erp-demo-file__input"
          accept={accept}
          multiple={multiple}
          onChange={(e) => {
            const list = [...(e.target.files || [])]
            onChange(multiple ? [...(files || []), ...list] : list.slice(0, 1))
            e.target.value = ''
          }}
        />
        <span>Clique ou solte arquivos aqui</span>
      </div>
      {files?.length > 0 && (
        <ul className="erp-mock-filelist">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              {f.name}{' '}
              <button
                type="button"
                className="erp-mock-link"
                onClick={() => onChange(files.filter((_, j) => j !== i))}
              >
                remover
              </button>
            </li>
          ))}
        </ul>
      )}
    </Field>
  )
}

/** Seção de formulário multi-seção (L2 form-multisection / L1 form-layout). */
export function DemoFormSection({ step, title, subtitle, children }) {
  return (
    <section className="erp-form-section">
      <header className="erp-form-section__head">
        <h2>
          {step != null && <span className="erp-form-section__step">{step}</span>}
          {title}
        </h2>
        {subtitle && <p>{subtitle}</p>}
      </header>
      <div className="erp-form-section__body">{children}</div>
    </section>
  )
}

/** Barra de ações L2 form-actions. */
export function DemoFormActions({ onConfirm, onCancel, confirmLabel = 'Salvar', cancelLabel = 'Limpar' }) {
  return (
    <div className="erp-mock-head erp-mock-head--actions erp-form-actions">
      <DemoBtn variant="ghost" onClick={onCancel}>
        {cancelLabel}
      </DemoBtn>
      <DemoBtn variant="primary" onClick={onConfirm}>
        {confirmLabel}
      </DemoBtn>
    </div>
  )
}

